import React, { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router';

import { useListDomainsForWidgetQuery } from '../../graphql/domain.generated';
import { DonutChart } from '../shared/charts';
import { DonutDataItem } from '../shared/charts/chartConfigUtilService';
import { formatNumber } from '../shared/formatNumber';
import { useTranslation } from '../../i18n/useTranslation';
import { appendOtherSlice, WIDGET_CHART_COLORS, WIDGET_OTHER_COLOR, WIDGET_TOP_ITEMS_LIMIT } from './utils';
import { ChartWidgetCard } from './ChartWidgetCard';
import { useEntityRegistry } from '../useEntityRegistry';
import { EntityType } from '../../types.generated';
import { mergeDashboardFilters, useDashboardFilters } from './dashboardFilters';

// Upper bound on domains pulled back to build the donut. listDomains returns every
// matching domain (including ones with no attached entities), unlike the asset facet.
const DOMAINS_FETCH_LIMIT = 500;

export const DomainsWidget = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const entityRegistry = useEntityRegistry();
    const dashboardFilters = useDashboardFilters();

    const {
        data: domainsData,
        previousData: previousDomainsData,
        loading: domainsLoading,
    } = useListDomainsForWidgetQuery({
        variables: {
            input: {
                query: '*',
                start: 0,
                count: DOMAINS_FETCH_LIMIT,
                orFilters: mergeDashboardFilters(dashboardFilters),
            },
        },
        fetchPolicy: 'no-cache',
    });

    // While the filter-driven refetch is in flight Apollo sets data=undefined.
    // Fall back to the last successful response so the chart stays visible
    // and only the header spinner signals that loading is happening.
    const effectiveData = domainsData ?? previousDomainsData;

    // Total across ALL matching domains (server-reported), including domains with no
    // attached entities -- this drives the center count and stays correct even when a
    // selected domain has zero entities (the case that previously rendered "No data available").
    const totalDomainsCount = effectiveData?.listDomains?.total ?? 0;

    const domainsChartData = useMemo(() => {
        const domains = effectiveData?.listDomains?.domains ?? [];
        if (domains.length === 0) {
            return [];
        }

        const sortedDomains = [...domains].sort((a, b) => (b.entities?.total ?? 0) - (a.entities?.total ?? 0));
        const topDomains = sortedDomains.slice(0, WIDGET_TOP_ITEMS_LIMIT);
        const remainingDomains = sortedDomains.slice(WIDGET_TOP_ITEMS_LIMIT);

        const chartData: DonutDataItem[] = topDomains.map((domain, index) => ({
            name: domain.properties?.name || domain.urn.split(':').pop() || t('Unknown Domain'),
            y: domain.entities?.total ?? 0,
            color: WIDGET_CHART_COLORS[index % WIDGET_CHART_COLORS.length],
            urn: domain.urn,
        }));

        const otherTotal = remainingDomains.reduce((sum, domain) => sum + (domain.entities?.total ?? 0), 0);
        appendOtherSlice(chartData, otherTotal, t('Other'));

        // Every selected domain may have zero entities (donut slices would all be y=0 and
        // render nothing). Keep the ring visible with a single neutral slice -- `y: 1` only
        // draws the ring geometry, while `displayY: 0` shows the honest "0" in the
        // label/tooltip -- instead of collapsing to an empty "No data available" state.
        if (chartData.every((slice) => slice.y === 0)) {
            return [{ name: t('No entities found'), y: 1, displayY: 0, color: WIDGET_OTHER_COLOR }];
        }

        return chartData;
    }, [effectiveData, t]);

    // Stable reference so DonutChart's memoized config doesn't change every render.
    const handlePointClick = useCallback(
        (_name: string, urn?: string) => {
            if (urn) {
                history.push(entityRegistry.getEntityUrl(EntityType.Domain, urn));
            }
        },
        [history, entityRegistry],
    );

    return (
        <ChartWidgetCard title={t('Domains')} loading={domainsLoading} isEmpty={domainsChartData.length === 0}>
            <DonutChart
                height={310}
                seriesName={t('Entities')}
                data={domainsChartData}
                subText={t('Total Domains')}
                centerText={String(formatNumber(totalDomainsCount))}
                onPointClick={handlePointClick}
            />
        </ChartWidgetCard>
    );
};
